<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onKeyStroke } from '@vueuse/core';
import { useGgFollowupSelection } from 'dashboard/composables/useGgFollowup';
import GgSupportAPI from 'dashboard/api/ggSupport';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import GgSupportForm from './GgSupportForm.vue';
import GgSupportSelect from './GgSupportSelect.vue';
import GgSupportOperation from './GgSupportOperation.vue';
import GgSupportCreationDialog from './GgSupportCreationDialog.vue';
import { emptyForm, invalidFields, serializeForm, restoreForm } from './form';

const props = defineProps({ conversationId: { type: Number, required: true } });
const { t, locale } = useI18n();
const { close, setLocked } = useGgFollowupSelection();
const context = ref(null);
const busy = ref(false);
const error = ref('');
const preview = ref(null);
const ticketForm = ref(emptyForm());
const formErrors = ref({});
const panelRef = ref(null);
const responsibleId = ref('');
const creationOperation = ref(null);
const reviewedDescription = ref('');
const showCreationConfirmation = ref(false);
const MAX_DESCRIPTION = 120000;
const POLL_INTERVAL = 3000;
let pollTimer;
let disposed = false;
let generation = 0;
const submitted = computed(
  () => creationOperation.value && creationOperation.value.state !== 'draft'
);
const canRegister = computed(
  () =>
    preview.value &&
    context.value?.registration_available &&
    creationOperation.value?.state === 'draft'
);
const descriptionValid = computed(() => {
  const length = [...reviewedDescription.value.trim()].length;
  return (
    length > 0 &&
    length <= MAX_DESCRIPTION &&
    !reviewedDescription.value.includes('\0')
  );
});
const canPrepare = computed(() => context.value && !busy.value);
const dateLabel = value =>
  new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
const showError = exception => {
  const code = exception.response?.data?.code;
  const errors = {
    INVALID_RANGE: t('GG_FOLLOWUP.ERRORS.INVALID_RANGE'),
    RANGE_TOO_LARGE: t('GG_FOLLOWUP.ERRORS.RANGE_TOO_LARGE'),
    TICKET_REQUIRED: t('GG_SUPPORT.ERRORS.TICKET_REQUIRED'),
    INVALID_OPERATION: t('GG_SUPPORT.ERRORS.INVALID_DETAILS'),
    INVALID_DETAILS: t('GG_SUPPORT.ERRORS.INVALID_DETAILS'),
    REQUIRED_FIELDS: t('GG_SUPPORT.ERRORS.REQUIRED_FIELDS'),
    TYPE_FIELDS_INVALID: t('GG_SUPPORT.CREATION.ERRORS.TYPE_FIELDS_INVALID'),
    REFERENCE_NOT_ELIGIBLE: t(
      'GG_SUPPORT.CREATION.ERRORS.REFERENCE_NOT_ELIGIBLE'
    ),
    ORDER_ITEM_NOT_ELIGIBLE: t(
      'GG_SUPPORT.CREATION.ERRORS.ORDER_ITEM_NOT_ELIGIBLE'
    ),
    EQUIPMENT_NOT_ELIGIBLE: t(
      'GG_SUPPORT.CREATION.ERRORS.EQUIPMENT_NOT_ELIGIBLE'
    ),
    SCHEDULE_PARTICIPANT_NOT_ELIGIBLE: t(
      'GG_SUPPORT.CREATION.ERRORS.SCHEDULE_PARTICIPANT_NOT_ELIGIBLE'
    ),
    SUPPORT_CATALOG_UNAVAILABLE: t('GG_SUPPORT.CATALOG_SEARCH.UNAVAILABLE'),
    SUPPORT_CATALOG_INVALID_RESPONSE: t('GG_SUPPORT.CATALOG_SEARCH.FAILED'),
    CUSTOMER_REQUIRED: t('GG_SUPPORT.ERRORS.CUSTOMER_REQUIRED'),
    OPPORTUNITY_REQUIRED: t('GG_SUPPORT.ERRORS.OPPORTUNITY_REQUIRED'),
    ORDER_REQUIRED: t('GG_SUPPORT.ERRORS.ORDER_REQUIRED'),
    EVENT_REQUIRED: t('GG_SUPPORT.ERRORS.EVENT_REQUIRED'),
    REPRESENTATIVE_REQUIRED: t('GG_SUPPORT.ERRORS.REPRESENTATIVE_REQUIRED'),
    SCHEDULE_REQUIRED: t('GG_SUPPORT.ERRORS.SCHEDULE_REQUIRED'),
    SCHEDULE_INVALID: t('GG_SUPPORT.ERRORS.SCHEDULE_INVALID'),
    DATE_INVALID: t('GG_SUPPORT.ERRORS.DATE_INVALID'),
    CUSTOMER_DIRECTORY_UNAVAILABLE: t('GG_SUPPORT.CUSTOMER_SEARCH.UNAVAILABLE'),
    CUSTOMER_DIRECTORY_INVALID_RESPONSE: t('GG_SUPPORT.CUSTOMER_SEARCH.FAILED'),
    CUSTOMER_ACCESS_DENIED: t('GG_SUPPORT.CUSTOMER_SEARCH.ACCESS_DENIED'),
    CUSTOMER_NOT_FOUND: t('GG_SUPPORT.CUSTOMER_SEARCH.NOT_FOUND'),
    SUPPORT_ACCESS_DENIED: t('GG_SUPPORT.CONTEXT.ACCESS_DENIED'),
    SUPPORT_CONTEXT_UNAVAILABLE: t('GG_SUPPORT.CONTEXT.UNAVAILABLE'),
    SUPPORT_CONTEXT_INVALID_RESPONSE: t('GG_SUPPORT.CONTEXT.UNAVAILABLE'),
    SUPPORT_RATE_LIMITED: t('GG_SUPPORT.CONTEXT.RATE_LIMITED'),
    RESPONSIBLE_REQUIRED: t('GG_SUPPORT.ERRORS.RESPONSIBLE_REQUIRED'),
    RESPONSIBLE_UNAVAILABLE: t('GG_SUPPORT.ERRORS.RESPONSIBLE_UNAVAILABLE'),
    DESCRIPTION_REQUIRED: t('GG_SUPPORT.CREATION.DESCRIPTION_REQUIRED'),
    STALE_PREVIEW: t('GG_SUPPORT.CREATION.STALE_PREVIEW'),
    IDEMPOTENCY_CONFLICT: t('GG_SUPPORT.CREATION.ERRORS.IDEMPOTENCY_CONFLICT'),
    CREATION_DISABLED: t('GG_SUPPORT.CREATION.ERRORS.WRITE_DISABLED'),
    INITIAL_STATE_INVALID: t('GG_SUPPORT.CREATION.CONFIRMATION.STATUS_PENDING'),
    SUPPORT_OPERATION_PENDING: t(
      'GG_SUPPORT_STATE.ERRORS.SUPPORT_OPERATION_PENDING'
    ),
  };
  error.value = errors[code] || t('GG_SUPPORT.ERRORS.REQUEST_FAILED');
};
const applyOperation = record => {
  creationOperation.value = record;
  if (record?.payload) {
    preview.value = record.payload;
    if (record.state !== 'draft') return;
    if (record.payload.contract_version !== 'chatwoot.support.draft.v6') {
      preview.value = null;
      creationOperation.value = null;
      return;
    }
    const fields = record.payload.draft.fields;
    responsibleId.value = String(record.payload.draft.responsible.id);
    ticketForm.value = restoreForm(record.payload.draft);
    reviewedDescription.value = fields.description;
  }
};
const shouldPoll = () => {
  const record = creationOperation.value;
  return (
    record &&
    (['queued', 'waiting', 'processing', 'unknown'].includes(record.state) ||
      (record.state === 'saved' && record.note_status === 'pending'))
  );
};
const loadOperation = async (id = undefined) => {
  clearTimeout(pollTimer);
  try {
    const { data } = await GgSupportAPI.operation(props.conversationId, id);
    if (disposed) return;
    applyOperation(data.operation);
  } catch {
    if (!disposed) error.value = t('GG_SUPPORT.CREATION.STATUS_UNAVAILABLE');
  } finally {
    if (!disposed && shouldPoll()) {
      pollTimer = setTimeout(
        () => loadOperation(creationOperation.value.operation_id),
        POLL_INTERVAL
      );
    }
  }
};
const requestConfirmation = () => {
  if (canRegister.value && descriptionValid.value && !busy.value)
    showCreationConfirmation.value = true;
};
const confirm = async initialStatus => {
  if (!canRegister.value || !descriptionValid.value || busy.value) return;
  const allowed = context.value.creation.initial_states || [
    { value: 'Backlog' },
  ];
  if (!allowed.some(state => state.value === initialStatus)) {
    error.value = t('GG_SUPPORT.CREATION.CONFIRMATION.STATUS_PENDING');
    return;
  }
  busy.value = true;
  showCreationConfirmation.value = false;
  error.value = '';
  const record = creationOperation.value;
  try {
    const { data } = await GgSupportAPI.confirm(
      props.conversationId,
      record.operation_id,
      record.preview_digest,
      reviewedDescription.value,
      initialStatus
    );
    if (!disposed) applyOperation(data.operation);
  } catch (exception) {
    if (!disposed) showError(exception);
  } finally {
    if (!disposed) {
      busy.value = false;
      await loadOperation(record.operation_id);
    }
  }
};
const resume = async () => {
  busy.value = true;
  error.value = '';
  try {
    const { data } = await GgSupportAPI.resume(
      props.conversationId,
      creationOperation.value.operation_id
    );
    if (!disposed) applyOperation(data.operation);
  } catch (exception) {
    if (!disposed) showError(exception);
  } finally {
    if (!disposed) {
      busy.value = false;
      await loadOperation(creationOperation.value.operation_id);
    }
  }
};
const loadContext = async () => {
  generation += 1;
  const requestGeneration = generation;
  busy.value = true;
  setLocked(true);
  error.value = '';
  try {
    const { data } = await GgSupportAPI.context(props.conversationId);
    if (requestGeneration === generation) {
      context.value = data;
      ticketForm.value = emptyForm(data.form_schema.defaults);
      responsibleId.value = data.responsible_suggestion
        ? String(data.responsible_suggestion.id)
        : '';
    }
  } catch (exception) {
    if (requestGeneration === generation) showError(exception);
  } finally {
    if (requestGeneration === generation) {
      busy.value = false;
      setLocked(false);
    }
  }
};
const newTicket = async () => {
  creationOperation.value = null;
  preview.value = null;
  reviewedDescription.value = '';
  context.value = null;
  await loadContext();
};
const prepare = async () => {
  if (!canPrepare.value) return;
  formErrors.value = invalidFields(context.value.form_schema, ticketForm.value);
  if (!responsibleId.value)
    formErrors.value.responsible_id = 'GG_SUPPORT.ERRORS.RESPONSIBLE_REQUIRED';
  if (Object.keys(formErrors.value).length) {
    error.value = t('GG_SUPPORT.ERRORS.REQUIRED_FIELDS');
    await nextTick();
    const firstInvalid = panelRef.value?.querySelector(
      'input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"]'
    );
    firstInvalid?.focus({ preventScroll: true });
    firstInvalid?.scrollIntoView({ block: 'center' });
    return;
  }
  generation += 1;
  const requestGeneration = generation;
  busy.value = true;
  setLocked(true);
  error.value = '';
  try {
    const { data } = await GgSupportAPI.preview(props.conversationId, {
      operation: 'create',
      ...serializeForm(ticketForm.value, context.value.form_schema),
      responsible_id: Number(responsibleId.value),
    });
    if (requestGeneration === generation) {
      preview.value = data.payload;
      creationOperation.value = null;
      if (data.operation) applyOperation(data.operation);
      context.value.requester = data.payload.requester;
      context.value.responsible_suggestion =
        data.payload.draft.responsible_suggestion;
      if (shouldPoll())
        await loadOperation(creationOperation.value.operation_id);
    }
  } catch (exception) {
    if (requestGeneration === generation) showError(exception);
  } finally {
    if (requestGeneration === generation) {
      busy.value = false;
      setLocked(false);
    }
  }
};
const previewRows = computed(() => {
  if (!preview.value) return [];
  const { fields, references } = preview.value.draft;
  if (!references) return [];
  const rows = ['type', 'priority', 'origin', 'description']
    .filter(key => fields[key])
    .map(key => ({ key, value: fields[key] }));
  const label = item =>
    [item.kind, item.code, item.id, item.name].filter(Boolean).join(' · ');
  if (references.customer)
    rows.push({ key: 'customer_reference', value: label(references.customer) });
  ['order', 'opportunity_id', 'event_id', 'representative_id'].forEach(key => {
    if (references[key]) rows.push({ key, value: label(references[key]) });
  });
  if (references.equipment?.length)
    rows.push({
      key: 'equipment_ids',
      value: references.equipment.map(label).join('\n'),
    });
  if (references.order_items?.length)
    rows.push({
      key: 'order_item_ids',
      value: references.order_items.map(label).join('\n'),
    });
  ['rental_demonstration', 'customer_is_representative'].forEach(key => {
    if (fields[key]) rows.push({ key, value: t('GG_SUPPORT.YES') });
  });
  if (fields.due_at)
    rows.push({ key: 'due_at', value: dateLabel(fields.due_at) });
  if (fields.schedule) {
    ['starts_at', 'ends_at'].forEach(key =>
      rows.push({ key, value: dateLabel(fields.schedule[key]) })
    );
    if (fields.schedule.location)
      rows.push({ key: 'location', value: fields.schedule.location });
    if (references.participants.length)
      rows.push({
        key: 'participant_user_ids',
        value: references.participants.map(person => person.name).join(', '),
      });
  }
  return rows;
});
watch(
  ticketForm,
  () => {
    formErrors.value = {};
    error.value = '';
  },
  { deep: true }
);
watch(responsibleId, () => {
  formErrors.value = {};
  error.value = '';
});
onKeyStroke('Escape', () => {
  if (!showCreationConfirmation.value) close();
});
watch([busy, preview, submitted], () =>
  setLocked(busy.value || Boolean(preview.value) || Boolean(submitted.value))
);
onMounted(async () => {
  await loadContext();
  if (!disposed) await loadOperation();
});
onUnmounted(() => {
  generation += 1;
  disposed = true;
  clearTimeout(pollTimer);
});
</script>

<template>
  <aside
    ref="panelRef"
    aria-labelledby="gg-support-title"
    class="fixed bottom-0 inset-x-0 z-40 h-[60dvh] flex flex-col bg-n-surface-1 border-t border-n-weak shadow-lg md:static md:h-full md:w-[400px] md:min-w-[400px] md:border-t-0 md:border-s md:shadow-none"
  >
    <header
      class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak shrink-0"
    >
      <h2
        id="gg-support-title"
        class="m-0 text-sm font-semibold text-n-slate-12"
      >
        {{ t('GG_SUPPORT.TITLE') }}
      </h2>
      <Button
        :aria-label="t('GG_FOLLOWUP.CLOSE')"
        :title="t('GG_FOLLOWUP.CLOSE')"
        icon="i-lucide-x"
        color="slate"
        variant="ghost"
        size="sm"
        @click="close"
      />
    </header>
    <div class="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-4">
      <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <div v-if="busy && !context" class="flex justify-center py-8">
        <Spinner />
      </div>
      <Button
        v-if="error && !context"
        :label="t('GG_FOLLOWUP.RETRY')"
        :disabled="busy"
        color="slate"
        variant="faded"
        @click="loadContext"
      />
      <GgSupportOperation
        v-if="submitted"
        :operation="creationOperation"
        :busy="busy"
        @resume="resume"
        @new-ticket="newTicket"
      />
      <template v-else-if="context">
        <div class="flex flex-col gap-1 text-xs text-n-slate-11">
          <span class="font-medium text-n-slate-12">{{
            t('GG_FOLLOWUP.CONVERSATION', { id: conversationId })
          }}</span>
          <span class="break-words">{{
            t('GG_FOLLOWUP.AUTHOR', { email: context.requester.email })
          }}</span>
        </div>
        <div
          role="note"
          class="rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-11"
        >
          {{ t('GG_SUPPORT.CREATION.FORM_ONLY') }}
        </div>
        <GgSupportForm
          v-if="!preview"
          v-model="ticketForm"
          :schema="context.form_schema"
          :conversation-id="conversationId"
          :customer-search-available="context.customer_search_available"
          :schedule-users="context.eligible_schedule_users"
          :errors="formErrors"
          :disabled="busy"
        />
        <div class="rounded-lg border border-n-weak p-3 flex flex-col gap-2">
          <GgSupportSelect
            v-if="!preview"
            id="gg-support-responsible"
            v-model="responsibleId"
            :label="t('GG_SUPPORT.RESPONSIBLE_SELECT')"
            :options="
              context.eligible_technicians.map(technician => ({
                value: String(technician.id),
                label: technician.name,
              }))
            "
            :disabled="busy"
            :error="
              formErrors.responsible_id ? t(formErrors.responsible_id) : ''
            "
          />
          <template v-else>
            <span class="text-xs font-semibold text-n-slate-12">{{
              t('GG_SUPPORT.RESPONSIBLE')
            }}</span>
            <span class="text-sm font-medium text-n-slate-12">{{
              preview.draft.responsible.name
            }}</span>
          </template>
          <p
            v-if="!preview && !responsibleId"
            class="m-0 text-xs text-n-slate-11"
          >
            {{ t('GG_SUPPORT.NO_RESPONSIBLE_SUGGESTION') }}
          </p>
          <p class="m-0 text-xs text-n-slate-11">
            {{ t('GG_SUPPORT.RESPONSIBLE_HINT') }}
          </p>
        </div>
        <template v-if="preview">
          <h3 class="m-0 text-sm font-semibold">
            {{ t('GG_SUPPORT.PREVIEW_TICKET') }}
          </h3>
          <dl class="m-0 flex flex-col gap-2 text-sm">
            <div v-for="row in previewRows" :key="row.key">
              <dt class="text-xs text-n-slate-11">
                {{ t(`GG_SUPPORT.FIELDS.${row.key}`) }}
              </dt>
              <dd class="m-0 whitespace-pre-wrap break-words">
                {{ row.value }}
              </dd>
            </div>
          </dl>
        </template>
        <div v-if="canRegister" class="flex flex-col gap-2">
          <TextArea
            id="gg-support-reviewed-description"
            v-model="reviewedDescription"
            :label="t('GG_SUPPORT.CREATION.DESCRIPTION')"
            :disabled="busy"
            custom-text-area-class="!h-40"
            resize
          />
          <p class="m-0 text-xs text-n-slate-11">
            {{ t('GG_SUPPORT.CREATION.REVIEW_HINT') }}
          </p>
          <p v-if="!descriptionValid" class="m-0 text-xs text-n-ruby-11">
            {{ t('GG_SUPPORT.CREATION.DESCRIPTION_REQUIRED') }}
          </p>
        </div>
        <p
          role="note"
          class="m-0 rounded-lg bg-n-amber-3 p-3 text-xs text-n-amber-11"
        >
          {{
            t(
              canRegister
                ? 'GG_SUPPORT.CREATION.CONFIRM_HINT'
                : 'GG_SUPPORT.CREATION.PREVIEW_SCOPE'
            )
          }}
        </p>
      </template>
    </div>
    <footer
      v-if="!submitted"
      class="flex flex-col gap-2 p-4 border-t border-n-weak shrink-0"
    >
      <Button
        v-if="!preview"
        :label="t('GG_SUPPORT.PREPARE_TICKET')"
        :is-loading="busy"
        :disabled="!canPrepare"
        @click="prepare"
      />
      <template v-else>
        <Button
          :label="t('GG_SUPPORT.BACK')"
          color="slate"
          variant="faded"
          :disabled="busy"
          @click="preview = null"
        />
        <Button
          :label="t('GG_SUPPORT.REGISTER_TICKET')"
          :disabled="!canRegister || !descriptionValid || busy"
          :is-loading="busy"
          @click="requestConfirmation"
        />
      </template>
    </footer>
  </aside>
  <GgSupportCreationDialog
    v-if="showCreationConfirmation"
    :responsible="preview.draft.responsible.name"
    :description="reviewedDescription"
    :allowed-states="context.creation.initial_states || []"
    @confirm="confirm"
    @close="showCreationConfirmation = false"
  />
</template>
